<template>
  <page-header />
  <a-layout class="signup-container">
    <a-layout-content>
      <a-card title="用户注册" class="signup-card">
        <template #extra>
          <a-button
              type="link"
              @click="toLogin()"
          >
            去登录
          </a-button>
        </template>
        <a-form
            :model="formState"
            name="signup"
            :label-col="{ span: 6 }"
            :wrapper-col="{ span: 16 }"
            @finish="onFinish"
            @finishFailed="onFinishFailed"
        >
          <!-- 动态渲染注册项 -->
          <template v-for="item in signupItems" :key="item.key">
            <template v-if="item.key === 'password' && item.switch">
              <a-form-item
                  :label="item.name"
                  :name="item.key"
                  :rules="getValidationRules(item.key, item)"
              >
                <a-input-password
                    v-model:value="formState[item.key]"
                    :placeholder="item.placeholder"
                />
              </a-form-item>
            </template>

            <template v-else-if="item.key === 'confirm_password' && item.switch">
              <a-form-item
                  :label="item.name"
                  :name="item.key"
                  :rules="getValidationRules(item.key, item)"
              >
                <a-input-password
                    v-model:value="formState[item.key]"
                    placeholder="请再次输入密码"
                />
              </a-form-item>
            </template>

            <template v-else-if="item.switch">
              <a-form-item
                  :label="item.name"
                  :name="item.key"
                  :rules="getValidationRules(item.key, item)"
              >
                <a-input
                    v-model:value="formState[item.key]"
                    :placeholder="item.placeholder"
                    :type="getInputType(item.key)"
                    autocomplete="off"
                />
              </a-form-item>
            </template>
          </template>

          <!-- 验证码 -->
          <a-form-item
              v-show="captchaProviderId > 0"
              label="验证码"
              name="captcha_answer"
              :rules="captchaProviderId > 0 ? [{ required: true, message: '请输入验证码！' }] : []"
          >
            <Captcha
                v-model:value="formState.captcha_answer"
                scene="sign_up"
                ref="captchaRef"
                @provider-id-updated="handleCaptchaProviderIdUpdated"
            />
          </a-form-item>

          <a-form-item :wrapper-col="{ offset: 6, span: 16 }" style="text-align: center">
            <a-button type="primary" html-type="submit" :loading="loading">注册</a-button>
          </a-form-item>
        </a-form>
      </a-card>
    </a-layout-content>
  </a-layout>
</template>

<script>
import { defineComponent, reactive, ref, onMounted } from 'vue'
import { message } from 'ant-design-vue'
import request from '@/utils/request'
import Captcha from "@/components/captcha.vue";
import PageHeader from "@/components/header.vue";
import {useRouter} from "vue-router";

export default defineComponent({
  components: {Captcha, PageHeader},
  setup() {
    const router = useRouter();

    const loading = ref(false)
    const signupItems = ref([])
    const passwordOptions = ref([])

    const captchaRef = ref(null)
    const captcha = ref({ switch: false })
    const captchaProviderId = ref(0)

    const formState = reactive({
      captcha_answer: ''
    })

    // 获取注册场景配置
    const fetchSignupScene = async () => {
      try {
        const response = await request.get('/site/scene/signup')
        const data = response.data
        if (!data.is_enable_sign_up) {
          message.error('当前系统未开放注册')
          return
        }
        signupItems.value = data.signup_items || []
        captcha.value = data.captcha || {}
        passwordOptions.value = data.password_options || []

        // 初始化表单字段
        signupItems.value.forEach(item => {
          formState[item.key] = ''
        })

      } catch (error) {
        message.error('获取注册配置失败')
        console.error(error)
      }
    }

    // 获取输入框类型
    const getInputType = (rule) => {
      if (rule === 'password') return 'password'
      if (rule === 'email') return 'email'
      if (rule === 'phone') return 'tel'
      return 'text'
    }

    // 密码确认验证
    const validatePasswordConfirm = (_, value) => {
      if (value && value !== formState.password) {
        return Promise.reject('两次输入的密码不一致')
      }
      return Promise.resolve()
    }

    // 密码复杂度验证
    const validatePassword = (_, value) => {
      if (!value) return Promise.reject('请输入密码')

      // 检查密码复杂度
      let errors = []
      if (passwordOptions.value.includes('AtLeast6') && value.length < 6) {
        errors.push('至少6个字符')
      }
      if (passwordOptions.value.includes('AtLeast8') && value.length < 8) {
        errors.push('至少8个字符')
      }
      if (passwordOptions.value.includes('Aa123') && !/[A-Za-z0-9]/.test(value)) {
        errors.push('包含大小写字母，数字')
      }
      if (passwordOptions.value.includes('SpecialChar') && !/[!@#$%^&*]/.test(value)) {
        errors.push('包含 !@#$%^&* 字符')
      }
      if (passwordOptions.value.includes('NoRepeat') && !/\d/.test(value)) {
        const hasRepeatingChars = /(.)\1{2,}/.test(value)
        if (hasRepeatingChars) {
          errors.push('不能有连续重复字符')
        }
      }

      if (errors.length > 0) {
        return Promise.reject(`密码需要${errors.join('、')}`)
      }
      return Promise.resolve()
    }

    // 获取验证规则
    const getValidationRules = (key, item) => {
      const rules = []

      // 必填规则
      if (item.required) {
        rules.push({
          required: true,
          message: `请输入${item.name || key}`
        })
      }

      // 根据字段类型添加特定规则
      switch (key) {
        case 'email':
          rules.push({
            type: 'email',
            message: '请输入有效的邮箱地址'
          })
          break

        case 'phone':
          rules.push({
            pattern: /^1[3-9]\d{9}$/,
            message: '请输入有效的手机号码'
          })
          break

        case 'password':
          rules.push({
            validator: validatePassword
          })
          break

        case 'confirm_password':
          rules.push({
            validator: validatePasswordConfirm
          })
          break
      }

      return rules
    }

    // 处理captchaProviderId更新，从插件传出来的，用于判断验证码是否有效
    const handleCaptchaProviderIdUpdated = (providerId) => {
      captchaProviderId.value = providerId;
    }

    // 提交注册
    const onFinish = async (values) => {
      try {
        loading.value = true
        const captchaKey = captchaRef.value?.captchaKey;
        const payload = {
          ...values,
          captcha_key: captchaKey,
          provider_id: captchaProviderId.value,
        }

        const res = await request.post('/auth/sign_up', payload)
        if (res.code !== 'success') {
          message.error(res.msg)
          // 刷新验证码
          if (captcha.value.switch) {
            await captchaRef.value.refreshCaptcha()
          }
          return
        } else {
          message.success('注册成功', 2, () => {
            router.push({ path: '/auth/login' });
          })
        }
      } catch (error) {
        console.error(error)
        message.error(error.response?.data?.message || '注册失败')
        // 刷新验证码
        if (captcha.value.switch) {
          await captchaRef.value.refreshCaptcha()
        }
      } finally {
        loading.value = false
      }
    }

    const onFinishFailed = (errorInfo) => {
      console.log('Failed:', errorInfo)
    }

    const toLogin = () => {
      router.push({ path: '/auth/login' });
    }

    onMounted(() => {
      fetchSignupScene()
    })

    return {
      loading,
      formState,
      signupItems,
      onFinish,
      onFinishFailed,
      captchaRef,
      captcha,
      captchaProviderId,
      handleCaptchaProviderIdUpdated,
      getInputType,
      validatePasswordConfirm,
      validatePassword,
      getValidationRules,
      toLogin
    }
  }
})
</script>

<style scoped>
.signup-container {
  min-height: 100vh;
  padding: 20px;
  background: #f0f2f5;
}

.signup-card {
  max-width: 600px;
  margin: 0 auto;
}
</style>
